import React, { useRef, useCallback } from 'react';
import Card, {
	CardBody,
	CardFooter,
	CardFooterRight,
	CardHeader,
	CardLabel,
	CardTitle,
} from './bootstrap/Card';
import Label from './bootstrap/forms/Label';
import Input from './bootstrap/forms/Input';
// import classNames from 'classnames';
import '../pages/presentation/demo-pages/customvset.css';
import Icon from '../components/icon/Icon';

const ImageUploader = ({
	label,
	selectedImages = [],
	onImageSelect,
	onImageDelete,
	accept = 'image/*',
	multiple = false,
	maxImages = 1,
	minDimensions = { width: 160, height: 40 },
	helperText = '',
	showAddButton = true,
	progress = [],
	typeError = false,
	darkModeStatus = false,
	themeStatus = false,
	inputRef,
	center = false,
	t = (text) => text,
	containerStyle = {},
	landscape = false,
}) => {
	const fileInputRef = inputRef ?? useRef(null);

	const tileWidth = landscape ? 360 : 128;
	const tileHeight = landscape ? 120 : 128;

	const handleAddButtonClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
			fileInputRef.current.click();
		}
	};

	const AddImageButton = useCallback(() => {
		if (!showAddButton || selectedImages.length >= maxImages) return null;

		const borderColor = darkModeStatus ? '#666' : '#8a8a8a';

		return (
			<div className='mb-3'>
				<div
					style={{
						width: `${tileWidth}px`,
						height: `${tileHeight}px`,
						border: `2px dashed ${borderColor}`,
						borderRadius: '10px',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						cursor: 'pointer',
						transition: 'all 0.3s ease',
						color: borderColor,
					}}
					className='add-image-button'
					onClick={handleAddButtonClick}>
					<Icon icon='PlusCircle' size='2x' className='mb-2' color={borderColor} />
					<span
						style={{
							fontSize: '12px',
							textAlign: 'center',
							color: borderColor,
							lineHeight: '1.2',
						}}>
						{t('Click to add')}
						<br />
						{t('Photo')}
					</span>
				</div>
			</div>
		);
	}, [showAddButton, selectedImages.length, maxImages, t, darkModeStatus, tileWidth, tileHeight]);

	const renderImageWithProgress = (element, index) => {
		const isFirstLine = index < 2;
		const progressData = progress.find((p) => p.id === element.id);

		return (
			<div
				key={element.id}
				className={'mb-3'}
				style={{
					width: `${tileWidth}px`,
					marginLeft: '0',
				}}>
				<div
					style={{
						borderRadius: '10px',
						overflow: 'hidden',
						position: 'relative',
						width: `${tileWidth}px`,
						height: `${tileHeight}px`,
					}}>
					<img
						src={element.file}
						alt=''
						width={tileWidth}
						height={tileHeight}
						className='img-fluid'
						style={{
							objectFit: 'contain',
							height: 'inherit',
							backgroundColor: '#4d69fa22',
							border: '1px solid #4d69fa55',
							borderRadius: '8px',
						}}
					/>
					<div className='image-delete-btn'>
						<button
							type='button'
							className='btn-icon'
							aria-label={t('Delete image')}
							title={t('Delete')}
							onClick={() => onImageDelete(index)}>
							<Icon icon='Delete' size='lg' />
						</button>
					</div>
				</div>
				{progressData?.uploading && (
					<div
						className='progress'
						style={{
							height: '5px',
							marginTop: '5px',
						}}>
						<div
							className='progress-bar progress-bar-striped progress-bar-animated'
							role='progressbar'
							style={{
								width: `${progressData.progress}%`,
							}}
						/>
					</div>
				)}
			</div>
		);
	};

	return (
		<div className='col-12' style={{ width: '100%' }}>
			<Label>
				{t(label)}
				<br />
				<p style={{ fontSize: '10px' }}>
					(Min: {minDimensions.width}px x {minDimensions.height}px)
				</p>
				{helperText ? (
					<p
						style={{
							fontSize: '11px',
							marginTop: '4px',
							color: themeStatus === 'dark' ? '#c9c3c3' : '#6c757d',
						}}>
						{t(helperText)}
					</p>
				) : null}
			</Label>
			<div
				className='col-12'
				style={{
					width: '100%',
					display: 'flex',
					flexDirection: landscape ? 'row' : center ? 'flex' : undefined,
					justifyContent: landscape ? 'flex-start' : center ? 'center' : undefined,
					alignItems: landscape ? 'center' : undefined,
					flexWrap: landscape ? 'nowrap' : containerStyle.flexWrap || 'wrap',
					...containerStyle,
				}}>
				{Array.isArray(selectedImages) && selectedImages.length > 0 ? (
					<div className='col-12 d-flex' style={{ width: 'fit-content' }}>
						{selectedImages.map(renderImageWithProgress)}
					</div>
				) : (
					<div className='col-12 d-flex' style={{ width: 'fit-content' }}>
						<AddImageButton />
					</div>
				)}
				{typeError.error && (
					<p
						style={{
							color: 'red',
							fontSize: '12px',
							width: '100%',
							textAlign: 'center',
						}}>
						{typeError.msg && typeError.msg !== t('*Required')
							? typeError.msg
							: t('*Required')}
					</p>
				)}
			</div>

			<div className='col-12'>
				<div className='row g-4'>
					<div className='col-12'>
						<Input
							ref={fileInputRef}
							type='file'
							id='filepath'
							autoComplete='photo'
							accept={accept}
							multiple={multiple}
							style={{ display: 'none' }}
							onChange={onImageSelect}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ImageUploader;
